import { LoaderCircle, RotateCw } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { focusIsLost, restoreFocus } from '@/lib/focus';

function focusMain() {
  document.getElementById('main')?.focus({ preventScroll: true });
}

interface RetryProps {
  title: string;
  error: Error;
  /** Whether the refetch started by Try again is running. Try again stays enabled and focused meanwhile. */
  retrying: boolean;
  onRetry: () => void;
}

interface RetryNoticeProps {
  /** Changes with each failure, for example the query's `errorUpdatedAt`. */
  errorKey: number;
  className?: string;
}

/**
 * Shown above content that is already on screen when refreshing it failed. The content stays as loaded, and Try
 * again refetches. The alert is rendered again for each new `errorKey`, so every failed retry is announced; when the
 * replaced alert held focus, focus moves to the new Try again button. When the notice disappears while it holds
 * focus, focus moves to the main region.
 */
export function RefreshFailedNotice({
  errorKey,
  className,
  error,
  ...alert
}: RetryProps & RetryNoticeProps) {
  const focusHandoff = useRef(false);
  return (
    <div className={className}>
      <RetryAlert key={errorKey} tone="warning" focusHandoffRef={focusHandoff} {...alert}>
        {error.message} What is shown here may be out of date.
      </RetryAlert>
    </div>
  );
}

/**
 * Shown in place of content that could not be loaded, with Try again. The alert is rendered again for each new
 * `errorKey`, so every failed retry is announced; when the replaced alert held focus, focus moves to the new Try
 * again button. When the notice disappears while it holds focus, focus moves to the main region.
 */
export function LoadFailedNotice({
  errorKey,
  className,
  error,
  ...alert
}: RetryProps & RetryNoticeProps) {
  const focusHandoff = useRef(false);
  return (
    <div className={className}>
      <RetryAlert key={errorKey} tone="danger" focusHandoffRef={focusHandoff} {...alert}>
        {error.message}
      </RetryAlert>
    </div>
  );
}

function RetryAlert({
  tone,
  focusHandoffRef,
  title,
  retrying,
  onRetry,
  children,
}: Omit<RetryProps, 'error'> & {
  tone: 'danger' | 'warning';
  /** Shared by the alerts of one notice: true from the unmount of an alert that held focus to the next mount. */
  focusHandoffRef: RefObject<boolean>;
  children: ReactNode;
}) {
  const alert = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  // A replacing alert mounts in the commit that unmounts the replaced one, after its cleanup, so focus moves to the
  // new button before any deferred focus fallback runs. The frame callback handles the notice going away.
  useLayoutEffect(() => {
    const node = alert.current;
    if (focusHandoffRef.current) {
      focusHandoffRef.current = false;
      restoreFocus(button.current);
    }
    return () => {
      if (!node?.contains(document.activeElement)) return;
      focusHandoffRef.current = true;
      requestAnimationFrame(() => {
        if (focusIsLost()) focusMain();
      });
    };
  }, [focusHandoffRef]);
  return (
    <div ref={alert}>
      <Callout
        tone={tone}
        role="alert"
        title={title}
        actions={
          <Button
            ref={button}
            size="sm"
            variant="secondary"
            aria-busy={retrying || undefined}
            onClick={() => {
              if (!retrying) onRetry();
            }}
          >
            {retrying ? (
              <LoaderCircle className="animate-spin" aria-hidden />
            ) : (
              <RotateCw aria-hidden />
            )}{' '}
            Try again
          </Button>
        }
      >
        {children}
      </Callout>
    </div>
  );
}
